/**
 * Catalogue behind the header's "Open in Claude" split button
 * (FileSpin-io/filespin-ui-2.0#998): which external agents it can open, and
 * the FileSpin starter prompts it pre-fills.
 *
 * Deliberately separate from the in-dashboard AI Agent starters
 * (filespin-mcp-server internal/prompts/starters.json). Those lean on context
 * the chat sends along — "the selected assets", "this collection" — which an
 * external agent never sees. Here every prompt stands on its own and names
 * FileSpin, because the user's Claude may have several connectors and has to
 * pick ours. The `requires` tags are the same ones, evaluated by the same
 * `passesRequires`.
 *
 * Pure data, no React, so it stays unit-testable.
 */
import type { StarterRequirement } from 'src/chat/types';
import { config } from 'src/utils/config';

export type ExternalAgentId = 'claude' | 'chatgpt';

export interface ExternalAgent {
  id: ExternalAgentId;
  name: string;
  /** Opens a new conversation with `prompt` in the input. Claude leaves it
   *  unsent behind its own "use caution" banner; the user presses send. */
  prefillUrl: (prompt: string) => string;
  /** Whether a connected app's name (GET /oauth/sessions `app_name`: the
   *  label, else the name the MCP broker reported) is this agent's web/desktop
   *  connector. Claude Code is excluded: its connector does not reach the
   *  claude.ai conversation the button opens. */
  matchesApp: (appName: string) => boolean;
}

export const EXTERNAL_AGENTS: Record<ExternalAgentId, ExternalAgent> = {
  claude: {
    id: 'claude',
    name: 'Claude',
    prefillUrl: (prompt) => `https://claude.ai/new?q=${encodeURIComponent(prompt)}`,
    matchesApp: (name) => /claude/i.test(name) && !/claude\s*code/i.test(name),
  },
  chatgpt: {
    id: 'chatgpt',
    name: 'ChatGPT',
    prefillUrl: (prompt) => `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`,
    matchesApp: (name) => /chatgpt|openai/i.test(name),
  },
};

/** Menu order. Claude first: it is the client the connector is listed for. */
export const EXTERNAL_AGENT_ORDER: ExternalAgentId[] = ['claude', 'chatgpt'];

export const DEFAULT_EXTERNAL_AGENT: ExternalAgentId = 'claude';

/** A stored value from an older build (or a hand-edited localStorage) falls
 *  back to the default rather than rendering a button that opens nothing. */
export function resolveExternalAgent(id: string | undefined | null): ExternalAgent {
  return EXTERNAL_AGENTS[id as ExternalAgentId] ?? EXTERNAL_AGENTS[DEFAULT_EXTERNAL_AGENT];
}

/** `connected` / `not_connected` once the user's connected apps are known;
 *  `unknown` while loading or when the list failed, which behaves as before
 *  (open the agent) rather than nagging a user who may well be connected. */
export type ConnectorStatus = 'connected' | 'not_connected' | 'unknown';

export function connectorStatus(
  agent: ExternalAgent,
  apps: { app_name: string; is_scheduler?: boolean }[] | undefined,
): ConnectorStatus {
  if (!apps) return 'unknown';
  return apps.some((a) => !a.is_scheduler && agent.matchesApp(a.app_name))
    ? 'connected'
    : 'not_connected';
}

/** Ids double as keys into messages.ts (`prompt_<id>_label` / `_text`). */
export type ExternalPromptId =
  | 'get_started'
  | 'weekly_uploads'
  | 'missing_metadata'
  | 'share_approvals'
  | 'campaign_links';

export interface ExternalPrompt {
  id: ExternalPromptId;
  requires?: StarterRequirement[];
}

/** What the main half of the split button sends. Not in the menu list, so
 *  the menu adds something the button doesn't already do. */
export const DEFAULT_PROMPT_ID: ExternalPromptId = 'get_started';

export const EXTERNAL_PROMPTS: ExternalPrompt[] = [
  { id: 'weekly_uploads' },
  { id: 'missing_metadata', requires: ['schema:active'] },
  { id: 'share_approvals' },
  { id: 'campaign_links', requires: ['addon:DELIVERY_INSIGHTS'] },
];

/** The MCP connector setup guide on the developer hub. */
export const MCP_SETUP_URL =
  config.mcpDocsUrl || 'https://developers.filespin.io/api-reference/tools/mcp-server/';
